import React, { useEffect, useMemo, useState } from 'react';
import {
  Mail, Plus, RefreshCw, Copy, ChevronLeft, ChevronRight, Info, FileText, Server, CheckCircle,
  CheckCircle2, CircleSlash,
} from 'lucide-react';
import http from '../api/http';
import BlueHeader from '../components/BlueHeader';
import {
  Avatar, Field, IdChip, ListCard, Modal, Notice, RowActions, StatCard, StatusBadge, Switch, Table,
  btnGhost, iconBtn, inputCls, rowCls, tdCls,
} from '../components/ui';

const TABS = [
  { id: 1, label: 'Informations' },
  { id: 2, label: 'Template' },
  { id: 3, label: 'Configuration SMTP' },
];

const Mailing = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [duplicatingItem, setDuplicatingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState(1);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/mailing-config');
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const stats = useMemo(() => {
    const actifs = data.filter((i) => i.actif).length;
    return { total: data.length, actifs, inactifs: data.length - actifs };
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((i) => [i.type_evenement, i.sujet, i.smtp_host].some((v) => (v || '').toLowerCase().includes(q)));
  }, [data, search]);

  const openModal = (item, { edit = false, duplicate = false } = {}) => {
    setEditingItem(edit ? item : null);
    setDuplicatingItem(duplicate ? item : null);
    setActiveTab(1);
    setShowModal(true);
  };

  const handleCreate = () => {
    openModal(null);
    setFormData({ actif: 1 });
  };

  const handleEdit = (item) => {
    openModal(item, { edit: true });
    setFormData({ ...item });
  };

  const handleDuplicate = (item) => {
    openModal(item, { duplicate: true });
    setFormData({
      ...item,
      idmailing_config: undefined, // pas d'ID : une nouvelle ligne sera créée
      sujet: (item.sujet || '') + ' (copie)',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette configuration de mailing ?')) return;
    try {
      await http.delete(`/api/mailing-config/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) await http.put(`/api/mailing-config/${editingItem.idmailing_config}`, formData);
      else await http.post('/api/mailing-config', formData);
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const set = (key) => (e) => setFormData({ ...formData, [key]: e.target.value });

  const modalTitle = duplicatingItem ? 'Dupliquer la configuration' : editingItem ? 'Modifier la configuration' : 'Créer une configuration';
  const confirmLabel = duplicatingItem ? 'Dupliquer' : editingItem ? 'Enregistrer' : 'Créer';
  const confirmIcon = duplicatingItem ? Copy : editingItem ? CheckCircle : Plus;

  return (
    <div className="min-h-full bg-slate-50 dark:bg-slate-950">
      <BlueHeader
        title="Mailing"
        subtitle="Configuration du mailing"
        icon={Mail}
        showBack={false}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
        ]}
      />

      <div className="space-y-6 p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Mail} label="Configurations" value={stats.total} />
          <StatCard icon={CheckCircle2} label="Actives" value={stats.actifs} />
          <StatCard icon={CircleSlash} label="Inactives" value={stats.inactifs} />
        </div>

        <ListCard
          count={filtered.length} singular="configuration affichée" plural="configurations affichées"
          search={search} onSearch={setSearch} placeholder="Rechercher par événement, sujet, SMTP"
          loading={loading} hasData={data.length > 0} icon={Mail}
          emptyTitle="Aucune configuration de mailing" emptyText="Créez votre première configuration pour envoyer des emails automatiques."
          onCreate={handleCreate} createLabel="Créer une configuration" createIcon={Plus}
        >
          <Table heads={['Événement', 'Sujet', 'Serveur SMTP', 'Statut', 'Actions']}>
            {filtered.map((item) => (
              <tr key={item.idmailing_config} className={rowCls}>
                <td className={tdCls}>
                  <div className="flex items-center gap-3">
                    <Avatar icon={Mail} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.type_evenement}</p>
                      <p className="mt-0.5"><IdChip>#{item.idmailing_config}</IdChip></p>
                    </div>
                  </div>
                </td>
                <td className={`${tdCls} max-w-xs truncate text-sm text-slate-700 dark:text-slate-300`}>{item.sujet || '-'}</td>
                <td className={`${tdCls} text-sm text-slate-600 dark:text-slate-300`}>{item.smtp_host || 'Par défaut'}</td>
                <td className={tdCls}><StatusBadge active={!!item.actif} /></td>
                <td className={tdCls}>
                  <RowActions onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item.idmailing_config)}>
                    <button type="button" onClick={() => handleDuplicate(item)} className={iconBtn} title="Dupliquer">
                      <Copy className="h-4 w-4" />
                    </button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </Table>
        </ListCard>
      </div>

      {showModal && (
        <Modal
          size="lg"
          title={modalTitle}
          subtitle={editingItem ? editingItem.type_evenement : 'Événement, template et paramètres d’envoi'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          confirmLabel={confirmLabel}
          confirmIcon={confirmIcon}
          footerLeft={
            <>
              <button type="button" onClick={() => setActiveTab(Math.max(1, activeTab - 1))} disabled={activeTab === 1} className={btnGhost}>
                <ChevronLeft className="h-4 w-4" /> Précédent
              </button>
              <button type="button" onClick={() => setActiveTab(Math.min(3, activeTab + 1))} disabled={activeTab === 3} className={btnGhost}>
                Suivant <ChevronRight className="h-4 w-4" />
              </button>
            </>
          }
        >
          <div className="flex gap-1 border-b border-slate-200 px-6 dark:border-slate-800">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`-mb-px border-b-2 px-4 py-3 text-sm font-medium transition ${
                  activeTab === t.id
                    ? 'border-slate-900 text-slate-900 dark:border-white dark:text-white'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="space-y-5 p-6">
            {activeTab === 1 && (
              <>
                <Notice icon={Info} title="Informations générales">
                  Définissez le type d’événement pour lequel cette configuration s’applique (ex : conge, carriere, mobilite, paie).
                </Notice>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Type d’événement *">
                    <input type="text" className={inputCls} value={formData.type_evenement || ''} onChange={set('type_evenement')} required placeholder="Ex: conge, carriere, mobilite, paie" />
                  </Field>
                  <Field label="Sujet *">
                    <input type="text" className={inputCls} value={formData.sujet || ''} onChange={set('sujet')} required placeholder="Sujet de l'email" />
                  </Field>
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/40 sm:col-span-2">
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">Configuration active</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {formData.actif ? 'Les emails de cet événement sont envoyés' : 'Aucun email envoyé pour cet événement'}
                      </p>
                    </div>
                    <Switch checked={!!formData.actif} label="Configuration active" onChange={(v) => setFormData({ ...formData, actif: v ? 1 : 0 })} />
                  </div>
                </div>
              </>
            )}

            {activeTab === 2 && (
              <>
                <Notice icon={FileText} title="Template du corps">
                  <p>Utilisez des variables comme {'{{variable}}'} pour personnaliser le contenu. Ex : {'{{nom}}'}, {'{{type_evenement}}'}, {'{{date}}'}.</p>
                  <p className="mt-1.5">Pour les blocs conditionnels, utilisez {'{{#variable}}'}...{'{{/variable}}'}.</p>
                </Notice>
                <Field label="Template du corps *">
                  <textarea
                    className={`${inputCls} font-mono`}
                    value={formData.corps_template || ''}
                    onChange={set('corps_template')}
                    rows={12}
                    required
                    placeholder={'<div>Bonjour {{nom}}, votre {{type_evenement}} est disponible...</div>'}
                  />
                </Field>
              </>
            )}

            {activeTab === 3 && (
              <>
                <Notice icon={Server} title="Configuration SMTP">
                  Paramètres du serveur SMTP pour l’envoi des emails. Laissez vide pour utiliser la configuration par défaut.
                </Notice>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="SMTP Host">
                    <input type="text" className={inputCls} value={formData.smtp_host || ''} onChange={set('smtp_host')} placeholder="smtp.gmail.com" />
                  </Field>
                  <Field label="SMTP Port">
                    <input type="number" className={inputCls} value={formData.smtp_port || ''} onChange={set('smtp_port')} placeholder="587" />
                  </Field>
                  <Field label="SMTP Secure">
                    <select className={inputCls} value={formData.smtp_secure || 'tls'} onChange={set('smtp_secure')}>
                      <option value="none">None</option>
                      <option value="tls">TLS</option>
                      <option value="ssl">SSL</option>
                    </select>
                  </Field>
                  <Field label="SMTP User">
                    <input type="text" className={inputCls} value={formData.smtp_user || ''} onChange={set('smtp_user')} placeholder="email@example.com" />
                  </Field>
                  <Field label="SMTP Password">
                    <input type="password" autoComplete="new-password" className={inputCls} value={formData.smtp_password || ''} onChange={set('smtp_password')} />
                  </Field>
                  <Field label="From Email">
                    <input type="email" className={inputCls} value={formData.smtp_from_email || ''} onChange={set('smtp_from_email')} placeholder="noreply@example.com" />
                  </Field>
                  <Field label="From Name">
                    <input type="text" className={inputCls} value={formData.smtp_from_name || ''} onChange={set('smtp_from_name')} placeholder="ZEUS SIRH" />
                  </Field>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Mailing;